import React, { useCallback, useEffect, useState } from "react";

const API_BASE = "https://dailydish.in/api";

/**
 * Admin editor for an apartment hub's block/tower list.
 *
 * This list is the source of truth for what a customer may store in their
 * address. The address form renders it as buttons instead of a text box, which
 * is what stops one physical tower being saved as "G block", "G Block",
 * "block G", "g-block" and "G" — 29 spellings for ~7 towers in live data — and
 * lets the rider app group orders by tower at all.
 *
 * Type the blocks exactly as the society names them: "A", "AB", "Heather",
 * "G Block". There is no format rule, because consistency comes from everyone
 * choosing the same string off one list, not from the string's shape.
 *
 * ORDER MATTERS: it is the order a rider walks the society, and the rider app
 * sorts by position here rather than alphabetically (alphabetical is useless
 * once "AB" and "Heather" are in play).
 */
const HubBlocksEditor = ({ hubId, blocks = [], onChange }) => {
  const [draft, setDraft] = useState("");
  const [usage, setUsage] = useState({}); // block -> address count
  const [renaming, setRenaming] = useState(null); // { from, to }
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  // Address counts only exist for a hub that has been saved. On the create
  // form there is nothing to count yet.
  const loadUsage = useCallback(async () => {
    if (!hubId) return;
    try {
      const response = await fetch(`${API_BASE}/Hub/hubs/${hubId}/blocks`);
      if (!response.ok) return;
      const data = await response.json();
      const counts = {};
      (data.blocks || []).forEach((entry) => {
        counts[entry.block] = entry.addressCount;
      });
      setUsage(counts);
    } catch (error) {
      console.warn("[HubBlocksEditor] usage fetch failed:", error.message);
    }
  }, [hubId]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const addBlock = () => {
    const value = draft.trim().replace(/\s+/g, " ");
    if (!value) return;
    // Case-insensitive, because "G" and "g" would look like one block in this
    // list but be two different values in customers' addresses.
    if (blocks.some((b) => b.toLowerCase() === value.toLowerCase())) {
      setMessage({ type: "error", text: `"${value}" is already in the list` });
      return;
    }
    onChange([...blocks, value]);
    setDraft("");
    setMessage(null);
  };

  const removeBlock = (block) => {
    const count = usage[block] || 0;
    if (count > 0) {
      const ok = window.confirm(
        `${count} address${count === 1 ? "" : "es"} still use "${block}".\n\n` +
          `Removing it will not change those addresses — they will show under ` +
          `"Unassigned" in the rider app until each customer re-picks a block.\n\n` +
          `If you meant to correct the spelling, use Rename instead.\n\nRemove anyway?`,
      );
      if (!ok) return;
    }
    onChange(blocks.filter((b) => b !== block));
  };

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  /**
   * Rename goes through its own endpoint rather than being inferred from the
   * saved array, because a whole-list save cannot distinguish a rename from a
   * delete-plus-add — and the server needs to know, so it can carry every
   * address using the old name across to the new one. Without that cascade a
   * rename would silently invalidate those addresses and re-prompt each of
   * those customers for a change they never made.
   */
  const submitRename = async () => {
    const from = renaming?.from;
    const to = (renaming?.to || "").trim().replace(/\s+/g, " ");
    if (!from || !to || from === to) {
      setRenaming(null);
      return;
    }

    if (!hubId) {
      // Hub not saved yet, so no addresses can reference it — a local swap is
      // all a rename means here.
      onChange(blocks.map((b) => (b === from ? to : b)));
      setRenaming(null);
      return;
    }

    setBusy(true);
    try {
      const response = await fetch(
        `${API_BASE}/Hub/hubs/${hubId}/blocks/rename`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ from, to }),
        },
      );
      const data = await response.json();

      if (!response.ok) {
        setMessage({ type: "error", text: data.error || "Rename failed" });
        return;
      }

      onChange(data.blocks || []);
      setMessage({
        type: "success",
        text: `Renamed to "${to}". ${data.addressesUpdated} address${
          data.addressesUpdated === 1 ? "" : "es"
        } updated.`,
      });
      setRenaming(null);
      loadUsage();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <label style={{ fontWeight: 600, fontSize: 14, display: "block" }}>
        Blocks / Towers
      </label>
      <p style={{ fontSize: 12, color: "#666", margin: "4px 0 10px" }}>
        Enter them exactly as the society names them — <code>A</code>,{" "}
        <code>AB</code>, <code>Heather</code>. Customers pick from these buttons
        instead of typing. <strong>Order = the rider's walking order.</strong>
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addBlock();
            }
          }}
          placeholder="e.g. A"
          style={{
            flex: 1,
            height: 38,
            padding: "0 12px",
            borderRadius: 8,
            border: "1px solid #CCC",
          }}
        />
        <button
          type="button"
          onClick={addBlock}
          style={{
            height: 38,
            padding: "0 18px",
            borderRadius: 8,
            border: "none",
            background: "#6B8E23",
            color: "#FFF",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Add
        </button>
      </div>

      {blocks.length === 0 ? (
        <p style={{ fontSize: 13, color: "#999", fontStyle: "italic" }}>
          No blocks yet. Until you add some, customers at this hub will see a
          plain text box and their entries will not be consistent.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {blocks.map((block, index) => (
            <div
              key={block}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                borderRadius: 8,
                border: "1px solid #E0E0E0",
                background: "#FAFAFA",
              }}
            >
              <span style={{ color: "#999", fontSize: 12, width: 20 }}>
                {index + 1}
              </span>

              {renaming?.from === block ? (
                <>
                  <input
                    type="text"
                    autoFocus
                    value={renaming.to}
                    onChange={(e) =>
                      setRenaming({ from: block, to: e.target.value })
                    }
                    style={{
                      flex: 1,
                      height: 30,
                      padding: "0 8px",
                      borderRadius: 6,
                      border: "1px solid #6B8E23",
                    }}
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={submitRename}
                    style={smallButton("#6B8E23")}
                  >
                    {busy ? "Saving…" : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRenaming(null)}
                    style={smallButton("#999")}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontWeight: 600 }}>{block}</span>
                  {usage[block] > 0 && (
                    <span style={{ fontSize: 12, color: "#666" }}>
                      {usage[block]} address{usage[block] === 1 ? "" : "es"}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    style={smallButton("#BBB")}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === blocks.length - 1}
                    style={smallButton("#BBB")}
                    title="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => setRenaming({ from: block, to: block })}
                    style={smallButton("#4A78B5")}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    onClick={() => removeBlock(block)}
                    style={smallButton("#C0392B")}
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {message && (
        <p
          style={{
            marginTop: 10,
            fontSize: 13,
            color: message.type === "error" ? "#C0392B" : "#2E7D32",
          }}
        >
          {message.text}
        </p>
      )}
    </div>
  );
};

const smallButton = (background) => ({
  height: 28,
  padding: "0 10px",
  borderRadius: 6,
  border: "none",
  background,
  color: "#FFF",
  fontSize: 12,
  cursor: "pointer",
});

export default HubBlocksEditor;
